import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  validateMobileNumber, 
  formatMobileNumber, 
  sendOTP, 
  verifyOTP, 
  clearStoredOTP,
  validatePassword,
  validateName,
  formatCountdown
} from '../utils/otpUtils';
import SuccessModal from '../components/SuccessModal';
import './SignupPage.css';

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup, loading } = useAuth();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const sync = () => {
      document.documentElement.classList.toggle('auth-page-mobile-lock', mq.matches);
      document.documentElement.classList.toggle('auth-page-mobile-lock--signup', mq.matches);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
      document.documentElement.classList.remove('auth-page-mobile-lock');
      document.documentElement.classList.remove('auth-page-mobile-lock--signup');
    };
  }, []);

  const [otpData, setOtpData] = useState({
    otp: '',
    isOtpSent: false,
    isOtpVerified: false,
    timer: 0,
    attempts: 0,
    maxAttempts: 3
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  /** From POST .../whatsapp-otp/verify when server issues JWT (SIGNUP_REQUIRE_PHONE_VERIFICATION_TOKEN). */
  const [phoneVerificationToken, setPhoneVerificationToken] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'mobile') {
      const formattedValue = formatMobileNumber(value);
      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
      setPhoneVerificationToken(null);
      setOtpData((prev) => ({
        ...prev,
        otp: '',
        isOtpSent: false,
        isOtpVerified: false,
        timer: 0,
        attempts: 0,
      }));
      clearStoredOTP();
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleOtpChange = (e) => {
    const { value } = e.target;
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '');
    
    setOtpData(prev => ({
      ...prev,
      otp: digitsOnly.slice(0, 6) // Limit to 6 digits
    }));
    
    if (errors.otp) {
      setErrors(prev => ({
        ...prev,
        otp: ''
      }));
    }
  };

  const sendOtp = async () => {
    if (!formData.mobile.trim()) {
      setErrors(prev => ({ ...prev, mobile: 'Mobile number is required' }));
      return;
    }
    
    if (!validateMobileNumber(formData.mobile)) {
      setErrors(prev => ({ ...prev, mobile: 'Please enter a valid Pakistani mobile number' }));
      return;
    }

    try {
      const result = await sendOTP(formData.mobile);
      
      if (result.success) {
        setOtpData((prev) => ({
          ...prev,
          isOtpSent: true,
          timer: 60,
          attempts: 0,
        }));
        setPhoneVerificationToken(null);

        // Clear any previous mobile errors
        setErrors(prev => ({ ...prev, mobile: '' }));
        
        // Start countdown timer
        const countdown = setInterval(() => {
          setOtpData(prev => {
            if (prev.timer <= 1) {
              clearInterval(countdown);
              return { ...prev, timer: 0 };
            }
            return { ...prev, timer: prev.timer - 1 };
          });
        }, 1000);

      } else {
        setErrors(prev => ({ ...prev, mobile: result.message }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, mobile: 'Failed to send OTP. Please try again.' }));
    }
  };

  const verifyOtp = async () => {
    if (!otpData.otp.trim()) {
      setErrors(prev => ({ ...prev, otp: 'Please enter the OTP' }));
      return;
    }

    if (otpData.otp.length !== 6) {
      setErrors(prev => ({ ...prev, otp: 'Please enter a 6-digit OTP' }));
      return;
    }

    try {
      const result = await verifyOTP(otpData.otp, formData.mobile);
      
      if (result.success) {
        setOtpData((prev) => ({
          ...prev,
          isOtpVerified: true,
          attempts: 0,
        }));
        setPhoneVerificationToken(result.phoneVerificationToken || null);

        setErrors((prev) => ({ 
          ...prev, 
          otp: '',
          otpSuccess: 'Mobile number verified successfully! ✓'
        }));

        // Clear success message after 3 seconds
        setTimeout(() => {
          setErrors(prev => ({ ...prev, otpSuccess: '' }));
        }, 3000);

      } else {
        setOtpData((prev) => ({
          ...prev,
          attempts: prev.attempts + 1,
        }));
        setPhoneVerificationToken(null);

        setErrors((prev) => ({ ...prev, otp: result.message }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, otp: 'OTP verification failed. Please try again.' }));
    }
  };

  const resendOtp = async () => {
    if (otpData.attempts >= otpData.maxAttempts) {
      setErrors(prev => ({ ...prev, mobile: 'Maximum attempts reached. Please try again later.' }));
      return;
    }

    try {
      const result = await sendOTP(formData.mobile);
      
      if (result.success) {
        setOtpData((prev) => ({
          ...prev,
          timer: 60,
          attempts: prev.attempts + 1,
        }));
        setPhoneVerificationToken(null);

        // Start countdown timer
        const countdown = setInterval(() => {
          setOtpData(prev => {
            if (prev.timer <= 1) {
              clearInterval(countdown);
              return { ...prev, timer: 0 };
            }
            return { ...prev, timer: prev.timer - 1 };
          });
        }, 1000);

      } else {
        setErrors(prev => ({ ...prev, mobile: result.message }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, mobile: 'Failed to resend OTP. Please try again.' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Enhanced name validation
    const nameValidation = validateName(formData.name);
    if (!nameValidation.success) {
      newErrors.name = nameValidation.message;
    }
    
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!validateMobileNumber(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid Pakistani mobile number';
    } else if (!otpData.isOtpVerified) {
      newErrors.mobile = 'Please verify your mobile number with the WhatsApp code';
    } else {
      // Enhanced validation for both phone formats
      const phone = formData.mobile.trim();
      const isLocalFormat = /^0\d{10}$/.test(phone); // 03001234567
      const isInternationalFormat = /^\+92\d{10}$/.test(phone); // +923001234567
      
      if (!isLocalFormat && !isInternationalFormat) {
        newErrors.mobile = 'Phone number must be in format: 03001234567 or +923001234567';
      }
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.success) {
        newErrors.password = passwordValidation.message;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Send phone number as-is (backend now supports both formats)
      const phone = formData.mobile.trim();
      
      // Debug: Log the data being sent
      console.log('🔧 SignupPage - Signup Data:', {
        name: formData.name,
        email: formData.email,
        phone: phone,
        password: formData.password
      });
      
      const result = await signup(formData.name, formData.email, phone, formData.password, {
        phoneVerificationToken: phoneVerificationToken || undefined,
      });
      
      if (result.success) {
        // Show success modal instead of alert
        if (result.message) {
          setSuccessMessage(result.message);
          setShowSuccessModal(true);
        } else {
          // Custom success message for Super Wear
          setSuccessMessage('🎉 Welcome to Super Wear! Your account has been created successfully. Please check your email to verify your account and start your golden shopping journey! ✨');
          setShowSuccessModal(true);
        }
        // Don't navigate immediately, let user close modal first
      } else {
        // Handle field-specific errors
        if (result.fieldErrors) {
          setErrors({
            ...result.fieldErrors,
            general: result.error
          });
        } else {
          setErrors({ general: result.error });
        }
      }
    } catch (error) {
      console.error('🔧 SignupPage - Submit Error:', error);
      setErrors({ general: 'Something went wrong. Please try again.' });
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page-container">
        <div className="auth-page-content">
          <div className="auth-form-container">
            <div className="auth-form-header">
              <h2>Create Account</h2>
              <p>Start your golden shopping journey</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {errors.general && (
                <div className="error-message general-error">
                  {errors.general}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`input-field ${errors.name ? 'error' : ''}`}
                  placeholder="Enter your full name"
                  disabled={loading}
                />
                {errors.name && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="mobile">Mobile Number</label>
                <div className="mobile-input-group">
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleInputChange}
                    className={`input-field mobile-input ${errors.mobile ? 'error' : ''} ${otpData.isOtpVerified ? 'verified' : ''}`}
                    placeholder="Enter mobile number"
                    disabled={loading || otpData.isOtpVerified}
                  />
                  <button
                    type="button"
                    className={`otp-btn ${otpData.isOtpVerified ? 'verified' : ''}`}
                    onClick={otpData.isOtpSent ? resendOtp : sendOtp}
                    disabled={loading || !formData.mobile.trim() || otpData.timer > 0 || otpData.isOtpVerified}
                  >
                    {otpData.isOtpVerified ? '✓ Verified' : otpData.timer > 0 ? formatCountdown(otpData.timer) : otpData.isOtpSent ? 'Resend code' : 'Send WhatsApp code'}
                  </button>
                </div>
                {errors.mobile && (
                  <span className="error-message">{errors.mobile}</span>
                )}
                {errors.otpSuccess && (
                  <span className="success-message">{errors.otpSuccess}</span>
                )}
              </div>

              {otpData.isOtpSent && !otpData.isOtpVerified && (
                <div className="form-group otp-group">
                  <label htmlFor="otp">Enter 6-digit WhatsApp code</label>
                  <div className="otp-input-group">
                    <input
                      type="text"
                      id="otp"
                      value={otpData.otp}
                      onChange={handleOtpChange}
                      className={`input-field otp-input ${errors.otp ? 'error' : ''}`}
                      placeholder="Code from WhatsApp"
                      maxLength="6"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="verify-btn"
                      onClick={verifyOtp}
                      disabled={loading || !otpData.otp.trim() || otpData.otp.length !== 6 || otpData.attempts >= otpData.maxAttempts}
                    >
                      Verify
                    </button>
                  </div>
                  {errors.otp && (
                    <span className="error-message">{errors.otp}</span>
                  )}
                  <div className="otp-info">
                    <small>
                      Code sent on WhatsApp to <strong>{formData.mobile}</strong>. Open WhatsApp on that number;
                      if you do not receive it, try Resend after the timer.
                    </small>
                    <small className="format-hint">Format: 03001234567 or +923001234567</small>
                    {otpData.attempts > 0 && (
                      <small className="attempts-info">
                        Attempts: {otpData.attempts}/{otpData.maxAttempts}
                      </small>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`input-field ${errors.email ? 'error' : ''}`}
                  placeholder="Enter your email"
                  disabled={loading}
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`input-field password-field ${errors.password ? 'error' : ''}`}
                    placeholder="Enter your password"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={loading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} eye-icon`} aria-hidden />
                  </button>
                </div>
                {errors.password && (
                  <span className="error-message">{errors.password}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`input-field password-field ${errors.confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm your password"
                    disabled={loading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    disabled={loading}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <i
                      className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} eye-icon`}
                      aria-hidden
                    />
                  </button>
                </div>
                {errors.confirmPassword && (
                  <span className="error-message">{errors.confirmPassword}</span>
                )}
              </div>

              <button
                type="submit"
                className={`auth-submit ${loading ? 'loading' : ''}`}
                disabled={loading}
              >
                {loading ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <span>Creating Account...</span>
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="auth-footer">
              <p>
                Already have an account?{' '}
                <Link to="/login" className="switch-mode-btn">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>

        <div className="auth-page-decoration">
          <div className="decoration-circle circle-1"></div>
          <div className="decoration-circle circle-2"></div>
          <div className="decoration-circle circle-3"></div>
        </div>
      </div>
      
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/'); // Navigate to home after closing modal
        }}
        message={successMessage}
      />
    </div>
  );
};

export default SignupPage;